import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import EmailImageUpload from "../EmailImageUpload";
import { getImageUrl } from "../../../utils/imageHelper";

/**
 * 4Col_4Img_Title_price Component
 * Displays 4 product cards in a 2x2 grid layout
 * Each card contains: image, product name, and price
 */
const FourColFourImgTitlePrice = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    products = [],
    component_name = "4Col_4Img_Title_price",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localProducts, setLocalProducts] = useState(
    Array.isArray(products) && products.length > 0
      ? products
      : []
  );

  useEffect(() => {
    setLocalTitle(title || "");
    if (Array.isArray(products) && products.length > 0) {
      setLocalProducts(products);
    }
  }, [id, title, products]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      products: localProducts,
    });
    setIsEditing(false);
  };

  const handleProductUpdate = (index, field, value) => {
    const updated = [...localProducts];
    updated[index] = { ...updated[index], [field]: value };
    setLocalProducts(updated);
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component four-col-img-title-price-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit 4 Column Product Grid</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Title (Optional)</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter section title"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Products (4 items)</label>
          <div className="row">
            {localProducts.map((product, index) => (
              <div key={index} className="col-md-6 mb-3">
                <div
                  className="border rounded p-3"
                  style={{ backgroundColor: "#f8f9fa" }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Product {index + 1}</strong>
                  </div>

                  <div className="mb-2">
                    <label className="form-label small">Product Image</label>
                    <EmailImageUpload
                      initialImage={product.image}
                      onImageUpload={(img) =>
                        handleProductUpdate(index, "image", img)
                      }
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label small">Product Name</label>
                    <input
                      className="form-control form-control-sm"
                      value={product.name || ""}
                      onChange={(e) =>
                        handleProductUpdate(index, "name", e.target.value)
                      }
                      placeholder="Product Name"
                    />
                  </div>

                  <div>
                    <label className="form-label small">Price</label>
                    <input
                      className="form-control form-control-sm"
                      value={product.price || ""}
                      onChange={(e) =>
                        handleProductUpdate(index, "price", e.target.value)
                      }
                      placeholder="Price: ₹"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  return (
    <>
      {!previewMode && (
        <div className="email-component four-col-img-title-price mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tr>
          <td
            align="center"
            style={{
              padding: "32px 16px",
              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
            }}
          >
            {/* Title */}
            {localTitle && (
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "bold",
                  color: primaryColor || "#333333",
                  marginBottom: "24px",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                {localTitle}
              </div>
            )}

            {/* Product Grid - 2x2 Layout */}
            <table
              width="100%"
              cellPadding="0"
              cellSpacing="0"
              border="0"
              style={{
                maxWidth: "600px",
                margin: "0 auto",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              {/* Top Row - Products 1 and 2 */}
              <tr>
                {localProducts.slice(0, 2).map((product, idx) => {
                  const imageUrl = getImageUrl(product.image);
                  return (
                    <td
                      key={idx}
                      width="50%"
                      valign="top"
                      style={{
                        padding: "8px",
                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                      }}
                    >
                      <table
                        width="100%"
                        cellPadding="0"
                        cellSpacing="0"
                        border="0"
                        style={{
                          border: "1px solid #d4a574",
                          borderRadius: "4px",
                          overflow: "hidden",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        {/* Image */}
                        <tr>
                          <td
                            style={{
                              padding: "0",
                              textAlign: "center",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            <table
                              width="100%"
                              cellPadding="0"
                              cellSpacing="0"
                              border="0"
                              style={{
                                height: "200px",
                                backgroundColor: "#f5f5f5",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              <tr>
                                <td
                                  align="center"
                                  valign="middle"
                                  style={{
                                    width: "100%",
                                    height: "200px",
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  {imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt={product.name || `Product ${idx + 1}`}
                                      width="100%"
                                      height="200"
                                      style={{
                                        width: "100%",
                                        height: "200px",
                                        display: "block",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <img
                                      src="/img-preview.svg"
                                      alt="Product placeholder"
                                      width="80"
                                      height="80"
                                      style={{
                                        width: "80px",
                                        height: "80px",
                                        display: "block",
                                        opacity: 0.5,
                                      }}
                                    />
                                  )}
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>

                        {/* Product Name */}
                        <tr>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              backgroundColor: "#ffffff",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "500",
                                color: primaryColor || "#333333",
                                marginBottom: "8px",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              {product.name || "Product Name"}
                            </div>
                          </td>
                        </tr>

                        {/* Price */}
                        <tr>
                          <td
                            style={{
                              padding: "0 12px 12px 12px",
                              textAlign: "center",
                              backgroundColor: "#ffffff",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "14px",
                                color: primaryColor || "#333333",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              {product.price || "Price: ₹"}
                            </div>
                          </td>
                        </tr>
                      </table>
                    </td>
                  );
                })}
              </tr>

              {/* Bottom Row - Products 3 and 4 */}
              <tr>
                {localProducts.slice(2, 4).map((product, idx) => {
                  const actualIndex = idx + 2;
                  const imageUrl = getImageUrl(product.image);
                  return (
                    <td
                      key={actualIndex}
                      width="50%"
                      valign="top"
                      style={{
                        padding: "8px",
                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                      }}
                    >
                      <table
                        width="100%"
                        cellPadding="0"
                        cellSpacing="0"
                        border="0"
                        style={{
                          border: "1px solid #d4a574",
                          borderRadius: "4px",
                          overflow: "hidden",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        {/* Image */}
                        <tr>
                          <td
                            style={{
                              padding: "0",
                              textAlign: "center",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                height: "200px",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={product.name || `Product ${actualIndex + 1}`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                />
                              ) : (
                                <img
                                  src="/img-preview.svg"
                                  alt="Product placeholder"
                                  style={{
                                    maxHeight: "80px",
                                    opacity: 0.5,
                                  }}
                                />
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Product Name */}
                        <tr>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              backgroundColor: "#ffffff",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "500",
                                color: primaryColor || "#333333",
                                marginBottom: "8px",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              {product.name || "Product Name"}
                            </div>
                          </td>
                        </tr>

                        {/* Price */}
                        <tr>
                          <td
                            style={{
                              padding: "0 12px 12px 12px",
                              textAlign: "center",
                              backgroundColor: "#ffffff",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "14px",
                                color: primaryColor || "#333333",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              {product.price || "Price: ₹"}
                            </div>
                          </td>
                        </tr>
                      </table>
                    </td>
                  );
                })}
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </>
  );
};

export default FourColFourImgTitlePrice;

